/**
 * Date handling for a payroll app: every date here is a calendar day in
 * Philadelphia, never an instant. The pitfall is `new Date("2026-09-21")`,
 * which JavaScript reads as UTC midnight and shows as the 20th in the evening
 * on the US East coast. Date-only strings are therefore parsed by hand.
 */
import type { PayPeriod } from '@/types'

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/** Parses an API date (ISO with or without time) or an `MM/DD/YYYY` string as a local date. */
export function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  const dateOnly = DATE_ONLY.exec(value)
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** `YYYY-MM-DD` from the local calendar day, which the API's `{date:datetime}` routes accept. */
export function toIsoDate(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const SHORT: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: '2-digit' }
const LONG: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' }
const MONTH_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
const WEEKDAY_MONTH_DAY: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }

/** "MON, SEP 21" */
export function formatShortDate(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  return date ? date.toLocaleDateString('en-US', SHORT).toUpperCase() : ''
}

/** "MON, SEP 21, 2026" */
export function formatLongDate(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  return date ? date.toLocaleDateString('en-US', LONG).toUpperCase() : ''
}

/** "Sep 21" */
export function formatMonthDay(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  return date ? date.toLocaleDateString('en-US', MONTH_DAY) : ''
}

/** "Mon, Sep 21" */
export function formatWeekdayDate(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  return date ? date.toLocaleDateString('en-US', WEEKDAY_MONTH_DAY) : ''
}

/** "4:59 PM" */
export function formatClockTime(value: Date): string {
  return value.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/** Today as `YYYY-MM-DD` in local time. */
export function todayIso(): string {
  return toIsoDate(new Date())
}

export function addDays(value: Date, days: number): Date {
  const next = new Date(value)
  next.setDate(next.getDate() + days)
  return next
}

/** The Monday on or before the given day. */
export function startOfWeek(value: Date): Date {
  const day = value.getDay() // 0 = Sunday
  const back = day === 0 ? 6 : day - 1
  const monday = addDays(value, -back)
  monday.setHours(0, 0, 0, 0)
  return monday
}

export function isWeekend(value: Date): boolean {
  const day = value.getDay()
  return day === 0 || day === 6
}

/** Whole days from the period's first day to `day`, starting at 1; null when outside. */
export function periodDayNumber(period: PayPeriod, day: Date): number | null {
  const start = parseDate(period.startPeriodDate)
  const end = parseDate(period.endPeriodDate)
  if (!start || !end) return null
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate())
  const index = Math.round((dayStart.getTime() - start.getTime()) / 86_400_000) + 1
  const length = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
  return index >= 1 && index <= length ? index : null
}

/** How many days the period spans (normally 14). */
export function periodLength(period: PayPeriod): number {
  const start = parseDate(period.startPeriodDate)
  const end = parseDate(period.endPeriodDate)
  if (!start || !end) return 14
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
}

/**
 * When entries for this pay period close: the last Friday of the period at
 * 16:59, which is the client-side rule in utils/time.ts expressed as a date.
 */
export function entryDeadline(period: PayPeriod): Date | null {
  const end = parseDate(period.endPeriodDate)
  if (!end) return null
  let friday = new Date(end)
  while (friday.getDay() !== 5) friday = addDays(friday, -1)
  friday.setHours(16, 59, 0, 0)
  return friday
}
